import { Box, CircularProgress } from "@mui/material"
import { PrintOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"

import { useGoodsReceiptContext } from "../../../../contexts"
import { useGetGoodsReceiptPallets } from "../../../../hooks/useGetGoodsReceiptPallets"
import { useGoodsReceiptPalletActions } from "../../../../hooks/useGoodsReceiptPalletActions"
import { usePalletSearch } from "../../../../hooks/usePalletSearch"
import { PrintPutAwayOrderButton } from "../../../CheckedIn/components/FinalizeCheckInSuccessDialog/components/PrintPutAwayOrderButton"

import { StoredGoodsReceiptBuiltPalletsTable } from "./StoredGoodsReceiptBuiltPalletsTable"

import { Button } from "@/components/common/Button"
import SearchInput from "@/components/SearchInput"
import { PermissionActionType } from "@/constants/permissions"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { isAkrochemManagedWarehouse } from "@/screens/WarehouseReceiving/utils"

export const StoredBuiltPalletsTab: FC = () => {
  const { goodsReceipt } = useGoodsReceiptContext()
  const { hasPermissions } = usePermissionManager()
  const { pallets, isFetching } = useGetGoodsReceiptPallets(goodsReceipt.goodsReceiptId)

  const { searchTerm, setSearchTerm, filteredPallets } = usePalletSearch(pallets)

  const isAkrochemManaged = isAkrochemManagedWarehouse(goodsReceipt?.warehouseArea?.type)
  const canMaintain = hasPermissions([PermissionActionType.WAREHOUSE_RECEIVING_MAINTENANCE])

  const { handleQrPrint, handleQrPrintAll } = useGoodsReceiptPalletActions()

  if (isFetching && pallets.length === 0) {
    return (
      <Box className='flex h-full items-center justify-center'>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box className='flex min-h-0 grow flex-col gap-y-4' data-testid='stored-built-pallets-tab'>
      <Box className='flex items-center gap-4'>
        <SearchInput
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder='Search by Product ID, Lot #, or Reference'
          className='mr-auto max-w-[524px] flex-1'
          data-testid='pallets-search-input'
        />

        {isAkrochemManaged && canMaintain && (
          <PrintPutAwayOrderButton
            goodsReceipt={goodsReceipt}
            pallets={pallets}
            buttonProps={{
              appearance: "outlined",
              variant: "primary",
              startIcon: <PrintOutlined />,
              "data-testid": "print-put-away-order-button",
            }}
            buttonLabel='Print Put Away Order'
          />
        )}

        {isAkrochemManaged && (
          <Button
            appearance='outlined'
            startIcon={<PrintOutlined />}
            onClick={() => handleQrPrintAll(filteredPallets)}
            data-testid='print-all-qr-codes-button'
          >
            Print All QR Codes
          </Button>
        )}
      </Box>

      <StoredGoodsReceiptBuiltPalletsTable
        pallets={filteredPallets}
        isAkrochemManaged={isAkrochemManaged}
        isLoading={isFetching}
        onQrPrint={handleQrPrint}
      />
    </Box>
  )
}
